import update from 'immutability-helper';
import uuidv1 from 'uuid/v1';
import { toast } from 'react-toastify';

import { 
  LESSON_CONTENT_ADD_FORM_ITEM,
  LESSON_CONTENT_MOVE_FORM_ITEM, 
  LESSON_CONTENT_SET_EDITING_FORM_ITEM_INDEX,
  LESSON_CONTENT_SAVE_FORM_ITEM_CHANGES,
  LESSON_CONTENT_DELETE_FORM_ITEM,
  LESSON_CONTENT_SET_FORM_ITEM_DELTA,
  LESSON_CONTENT_SET_FORM_ITEMS,
  LESSON_CONTENT_SET_IS_SAVED,
  LESSON_CONTENT_SET_LESSON_ID_OF_COPIED,
  LESSON_CONTENT_DELETE_LESSON_ID_OF_COPIED,
  LESSON_CONTENT_BLOCK_COPY,
  LESSON_CONTENT_BLOCK_PASTE,
  LESSON_CONTENT_INSERT_FORM_ITEM,
  LESSON_CONTENT_UPDATE_FORM_ITEM,
  LESSON_CONTENT_DUPLICATE_FORM_ITEM
} from '../actions/type';

/** Deep-copies a block and regenerates every id so answers do not collide with the original. */
const cloneBlock = (source) => {
  const id = uuidv1();
  const block = JSON.parse(JSON.stringify(source));
  if (block.id) block.id = id;
  if (block.componentName === 'FindCompliance') {
    [block.questionOptions1, block.questionOptions2].forEach(list => (list || []).forEach(q => {
      const uuid = `${uuidv1()}${Math.round(Math.random() * 1000)}`;
      q.input.attributes.id = uuid;
      q.label.attributes.htmlFor = uuid;
    }));
  } else if (Array.isArray(block.questionOptions)) {
    const isCheckboxes = block.componentName === 'Checkboxes';
    block.questionOptions.forEach(q => {
      const oldId = q.input.attributes.id;
      const uuid = `${uuidv1()}${Math.round(Math.random() * 1000)}`;
      if (isCheckboxes) {
        const at = (block.correctAnswers || []).indexOf(oldId);
        if (at >= 0) block.correctAnswers.splice(at, 1, uuid);
      } else {
        q.input.attributes.name = id;
        if (block.correctAnswer === oldId) block.correctAnswer = uuid;
      }
      q.input.attributes.id = uuid;
      q.label.attributes.htmlFor = uuid;
    });
  }
  return block;
};

const initialState = {
  formItems: [],
  editingFormItemIndex: -1,
  isSaved: true,
  lessonIdOfCopiedLessonContent: '',
  isCopied: false
};

const lessonContent = (state = initialState, action) => {
  switch (action.type) {
    case LESSON_CONTENT_SET_FORM_ITEMS:
      return {
        ...state,
        formItems: action.payload
      };

    case LESSON_CONTENT_ADD_FORM_ITEM:
      return update(state, {
        formItems: { $push: [action.payload] },
        isSaved: { $set: false }
      });

    case LESSON_CONTENT_MOVE_FORM_ITEM:
      const { from, to } = action.payload;
      const tmpFormItems = [...state.formItems];
      const cutOut = tmpFormItems.splice(from, 1)[0];
      tmpFormItems.splice(to, 0, cutOut);
      return {
        ...state,
        formItems: [...tmpFormItems],
        isSaved: false
      };

    case LESSON_CONTENT_SET_EDITING_FORM_ITEM_INDEX:
      return {
        ...state,
        editingFormItemIndex: action.payload
      };

    case LESSON_CONTENT_SAVE_FORM_ITEM_CHANGES:
      return update(state, {
        formItems: {
          [state.editingFormItemIndex]: { $set: action.payload }
        } 
      });

    case LESSON_CONTENT_SET_FORM_ITEM_DELTA:
      return update(state, {
        formItems: {
          [state.editingFormItemIndex]: {
            delta: { $set: action.payload }
          }
        }
      });

    case LESSON_CONTENT_DELETE_FORM_ITEM:
      return update(state, {
        formItems: {$splice: [[action.payload, 1]]},
        isSaved: { $set: false }
      });

    case LESSON_CONTENT_SET_IS_SAVED:
      return {
        ...state,
        isSaved: action.payload
      };

    case LESSON_CONTENT_SET_LESSON_ID_OF_COPIED:
      return {
        ...state,
        lessonIdOfCopiedLessonContent: action.payload
      };

    case LESSON_CONTENT_DELETE_LESSON_ID_OF_COPIED:
      return {
        ...state,
        lessonIdOfCopiedLessonContent: ''
      };

    case LESSON_CONTENT_BLOCK_COPY:
      const index = action.payload;
      const block = [...state.formItems][index];
      localStorage.setItem('block', JSON.stringify(block));
      toast.success('Copied!', { autoClose: 2500, closeButton: false });
      return {
        ...state,
        isCopied: true,
      };

    case LESSON_CONTENT_BLOCK_PASTE: {
      const nextIndex = action.payload + 1;
      const blockStr = localStorage.getItem('block');
      if (!blockStr) {
        toast.warn('Not copied yet!', { autoClose: 2500, closeButton: false });
        return state;
      }
      const pasted = cloneBlock(JSON.parse(blockStr));
      const newFormItems = [...state.formItems];
      newFormItems.splice(nextIndex, 0, pasted);
      localStorage.removeItem('block');
      toast.success('Pasted!', { autoClose: 2500, closeButton: false });
      return {
        ...state,
        isCopied: false,
        formItems: newFormItems
      };
    }

    case LESSON_CONTENT_INSERT_FORM_ITEM: {
      const { formItem, afterIndex } = action.payload;
      const items = [...state.formItems];
      const at = afterIndex >= 0 && afterIndex < items.length ? afterIndex + 1 : items.length;
      items.splice(at, 0, formItem);
      return { ...state, formItems: items, isSaved: false };
    }

    case LESSON_CONTENT_UPDATE_FORM_ITEM: {
      const { index, options } = action.payload;
      return update(state, {
        formItems: { [index]: { $set: options } },
        isSaved: { $set: false }
      });
    }

    case LESSON_CONTENT_DUPLICATE_FORM_ITEM: {
      const index = action.payload;
      const source = state.formItems[index];
      if (!source) return state;
      const items = [...state.formItems];
      items.splice(index + 1, 0, cloneBlock(source));
      return { ...state, formItems: items, isSaved: false };
    }

    default:
      return state;
  }
};

export default lessonContent;